/**
 * A plain MUI `<Table>` in the datagrid frame (hybrid-3c §9), for the screens
 * whose rows do not come from a react-admin `<List>`: dashboards, ledgers,
 * health checks. The frame, the header recipe (`custom.table`), the row hover
 * and the pagination strip are drawn here once, so no screen styles a table
 * cell of its own. Wide tables scroll sideways inside the frame.
 *
 * The caller passes `<TableHead>` and `<TableBody>` as children. With `empty`
 * the header stays and the empty state (a `DatagridEmpty`) is drawn under it,
 * still inside the frame.
 */

import React from 'react';
import { Box, Table, useTheme } from '@mui/material';

export interface DataTableProps {
  /** `<TableHead>` and `<TableBody>`. */
  children: React.ReactNode;
  /** The table's accessible name — usually the block heading above it. */
  label: string;
  /** Drawn under the header instead of rows when the table has nothing to show. */
  empty?: React.ReactNode;
  /** A pagination strip at the foot of the frame (a `TablePagination`). */
  footer?: React.ReactNode;
  /** From `sm` up, take the height the parent leaves and scroll the rows inside the frame with
   *  the header pinned (one-screen fit). The parent must be a flex column with a height. */
  fill?: boolean;
}

export const DataTable: React.FC<DataTableProps> = ({ children, label, empty, footer, fill = false }) => {
  const theme = useTheme();
  const { table } = theme.custom;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${table.headerBorder}`,
        borderRadius: theme.custom.radius.card,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        ...(fill && { [theme.breakpoints.up('sm')]: { flex: 1, minHeight: 0 } }),
      }}
    >
      <Box
        sx={{
          overflowX: 'auto',
          ...(fill && { [theme.breakpoints.up('sm')]: { flex: '0 1 auto', minHeight: 0, overflowY: 'auto' } }),
          '& .MuiTableCell-head': {
            backgroundColor: table.headerBg,
            borderBottom: `1px solid ${table.headerBorder}`,
            padding: '8px 14px',
            whiteSpace: 'nowrap',
            ...table.headCell,
            ...(fill && { position: 'sticky', top: 0, zIndex: 2 }),
          },
          '& .MuiTableCell-body': {
            fontSize: '0.8125rem',
            padding: '8px 14px',
            borderBottom: `1px solid ${table.rowBorder}`,
            color: theme.palette.text.primary,
            fontVariantNumeric: 'tabular-nums',
          },
          '& .MuiTableBody-root .MuiTableRow-root': {
            transition: 'background-color 0.14s ease',
            '&:hover': { backgroundColor: theme.palette.action.hover },
          },
          '& .MuiTableBody-root .MuiTableRow-root:last-of-type .MuiTableCell-body': { borderBottom: 'none' },
        }}
      >
        <Table size="small" aria-label={label}>
          {children}
        </Table>
        {empty}
      </Box>
      {footer && (
        <Box
          sx={{
            flexShrink: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
};

export default DataTable;
